import { Switch, FormGroup, Divider, FormControlLabel, FormHelperText, Autocomplete, TextField } from '@mui/material'
import {
  IntegrationConfig,
  IntegrationConfigSettingsFollowUpBossSyncSms,
  SyncDirectionEnum,
} from '@sendhub/integrations/api-client-typescript-axios'

import { useEffect, useState } from 'react'
import { updateIntegrationConfig } from '../../../store/integration-config/integration-configs.slice'
import { useAppDispatch } from '../../../store/root'
import { FormikProvider, useFormik } from 'formik'
import SelectAccount from '../../select-account'
import { FormSaveControls } from '../../save-status-indicator'
import { applyConfigUpdateError } from '../apply-config-update-error'
import { IntegrationsEnabledSwitch } from '../../integrations-enabled-switch'
import { selectAllServiceAccounts, selectServiceAccountsLoadingStatus } from '../../../store/service-account/service-accounts.slice'
import { useSelector } from 'react-redux'
import { sendhubApi as sh } from '../../../services/sendhub-api'
import * as yup from 'yup'
import { selectIntegrationGroupById } from '../../../store/integration-group/integration-groups.slice'
import { selectIntegrationById } from '../../../store/integration/integrations.slice'
import { getEnumValues } from '../../../../utils/get-enum-values'

export function isFubSyncSmsSettings(obj: unknown): obj is IntegrationConfigSettingsFollowUpBossSyncSms {
  return typeof obj === 'object' && obj !== null && 'syncDirection' in obj && !('groupBy' in obj)
}

export interface ConfigureFubSyncSmsProps {
  integrationConfig: IntegrationConfig
}

export function ConfigureFubSyncSms({ integrationConfig }: ConfigureFubSyncSmsProps) {
  const dispatch = useAppDispatch()
  const integration = useSelector(selectIntegrationById(integrationConfig.integrationId))
  const integrationGroup = useSelector(selectIntegrationGroupById(integration?.integrationGroupId || ''))
  const serviceAccounts = useSelector(selectAllServiceAccounts)
  const serviceAccountsLoadingStatus = useSelector(selectServiceAccountsLoadingStatus)

  // syncOptOuts is missing on configs saved before the setting existed; seed the
  // default (on) so the first save stores it and the server queues the backfill.
  const startingSettings = (): IntegrationConfigSettingsFollowUpBossSyncSms => {
    const settings: IntegrationConfigSettingsFollowUpBossSyncSms = isFubSyncSmsSettings(integrationConfig.settings)
      ? integrationConfig.settings
      : { syncDirection: SyncDirectionEnum.SendHubToFUB }
    return { ...settings, syncOptOuts: settings.syncOptOuts ?? true }
  }
  const [initialValues, setInitialValues] = useState({ ...integrationConfig, settings: startingSettings() })

  const validationSchema = yup.object({
    enabled: yup.boolean().default(false),
    integrationId: yup.string().required(),
    settings: yup.object({
      syncDirection: yup.string().oneOf(getEnumValues(SyncDirectionEnum)),
      syncOptOuts: yup.boolean(),
    }),
    serviceAccountId: yup
      .string()
      .required(`Must select a ${integrationGroup?.name || ''} Account`)
      .uuid(),
  })
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus, setFieldError, setFieldTouched, resetForm }) => {
      setStatus(null)
      try {
        await dispatch(updateIntegrationConfig({ id: values.id, integrationConfig: values })).unwrap()
        resetForm({ values, status: { type: 'success' } })
      } catch (error) {
        applyConfigUpdateError(error, { setStatus, setFieldError, setFieldTouched })
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({ values: { ...integrationConfig, settings: startingSettings() } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationConfig])

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', width: '100%', flexWrap: 'nowrap' }}>
        <SelectAccount integrationConfig={integrationConfig} />
        <Divider sx={{ marginY: 2 }} />

        <Autocomplete
          sx={{ marginBottom: 2 }}
          disableClearable
          openOnFocus
          id="syncDirection"
          style={{ display: 'none' }}
          value={formik.values.settings.syncDirection}
          onChange={(event, value) => formik.setFieldValue('settings.syncDirection', value)}
          options={[SyncDirectionEnum.SendHubToFUB, 'FUB to SendHub (Coming soon!)']}
          getOptionDisabled={(option) => option.includes('Coming soon')}
          renderInput={(params) => (
            <TextField
              {...params}
              name="syncDirection"
              label="Sync Direction"
              error={formik.touched.settings?.syncDirection && Boolean(formik.errors.settings?.syncDirection)}
              helperText={formik.touched.settings?.syncDirection && formik.errors.settings?.syncDirection}
            />
          )}
        />

        <FormGroup sx={{ marginBottom: 2 }}>
          <FormControlLabel
            control={
              <Switch
                id="syncOptOuts"
                checked={formik.values.settings.syncOptOuts !== false}
                onChange={(event) => formik.setFieldValue('settings.syncOptOuts', event.target.checked)}
              />
            }
            label="Sync opt-outs (STOP) to Follow Up Boss"
          />
          <FormHelperText>
            When a contact texts STOP, their Follow Up Boss person gets the tag "SendHub: Opted Out" and a timeline note. The tag is removed
            when they text START. Contacts who are already opted out are tagged when this is turned on.
          </FormHelperText>
        </FormGroup>

        <FormGroup>
          <IntegrationsEnabledSwitch />
        </FormGroup>
        <FormSaveControls />
      </form>
    </FormikProvider>
  )
}
